import { formatCurrency } from '../../i18n/i18n';
import type { SimulationState, GameSpeed } from '../../core/types';

export type HUDAction = 'createTeam' | 'startProject' | 'hire' | 'save' | 'load' | 'staffList' | 'company' | 'upgradeOffice' | 'train' | 'contracts' | 'teams' | 'projects' | 'loans' | 'log' | 'equipment' | 'achievements' | 'statistics';

export class HUD {
  private topBar: HTMLDivElement;
  private actionBar: HTMLDivElement;
  private projectBar: HTMLDivElement;
  private releasesBar: HTMLDivElement;

  private fundsEl: HTMLSpanElement;
  private dateEl: HTMLSpanElement;
  private staffEl: HTMLSpanElement;
  private levelEl: HTMLSpanElement;
  private speedButtons: HTMLButtonElement[] = [];

  // Action buttons (conditionally visible)
  private createTeamBtn!: HTMLButtonElement;
  private startProjectBtn!: HTMLButtonElement;
  private hireBtn!: HTMLButtonElement;
  private trainBtn!: HTMLButtonElement;
  private upgradeBtn!: HTMLButtonElement;
  private contractsBtn!: HTMLButtonElement;

  private onSpeedChange?: (speed: GameSpeed) => void;
  private onAction?: (action: HUDAction) => void;
  private lastProjectFingerprint: string = '';
  private hudContainer!: HTMLDivElement;
  onShipProject?: (projectId: string) => void;
  onProjectClick?: (projectId: string) => void;

  constructor(onSpeedChange?: (speed: GameSpeed) => void, onAction?: (action: HUDAction) => void) {
    this.onSpeedChange = onSpeedChange;
    this.onAction = onAction;

    // ─── Top Bar: funds, date, speed, stats ──────────────────
    this.topBar = document.createElement('div');
    this.topBar.className = 'hud';

    this.fundsEl = this.addSpan(this.topBar, 'hud__funds', '$0');
    this.fundsEl.setAttribute('aria-label', 'Company funds');
    this.dateEl = this.addSpan(this.topBar, 'hud__date', 'Year 1 - Week 1');

    this.topBar.appendChild(this.makeSpacer());

    // Speed controls
    const speedBox = document.createElement('div');
    speedBox.className = 'hud__speed';
    speedBox.setAttribute('role', 'group');
    speedBox.setAttribute('aria-label', 'Game speed');
    for (const s of [
      { label: 'Pause', speed: 0 as GameSpeed, icon: '⏸' },
      { label: '1x', speed: 1 as GameSpeed, icon: '▶' },
      { label: '2x', speed: 2 as GameSpeed, icon: '▶▶' },
      { label: '4x', speed: 4 as GameSpeed, icon: '▶▶▶' },
    ]) {
      const btn = document.createElement('button');
      btn.className = 'btn btn--icon';
      btn.textContent = s.icon;
      btn.setAttribute('aria-label', s.label);
      btn.setAttribute('data-speed', String(s.speed));
      btn.addEventListener('click', () => { this.onSpeedChange?.(s.speed); this.setActiveSpeed(s.speed); });
      speedBox.appendChild(btn);
      this.speedButtons.push(btn);
    }
    this.topBar.appendChild(speedBox);

    this.staffEl = this.addSpan(this.topBar, 'hud__stat', 'Staff: 1/4');
    this.levelEl = this.addSpan(this.topBar, 'hud__stat', 'Lv. 1');

    this.setActiveSpeed(1);

    // ─── Action Bar: game actions + view buttons ─────────────
    this.actionBar = document.createElement('div');
    this.actionBar.className = 'hud__actions';

    this.createTeamBtn = this.addActionBtn('+ Team', 'createTeam', 'btn--success');
    this.startProjectBtn = this.addActionBtn('+ Project', 'startProject', 'btn--primary');
    this.hireBtn = this.addActionBtn('Hire', 'hire');
    this.trainBtn = this.addActionBtn('Train', 'train');
    this.upgradeBtn = this.addActionBtn('Upgrade', 'upgradeOffice');
    this.contractsBtn = this.addActionBtn('Contracts', 'contracts');
    this.addActionBtn('Teams', 'teams');
    this.addActionBtn('Equip', 'equipment');
    this.addActionBtn('Loans', 'loans');

    // Separator
    const sep = document.createElement('div');
    sep.style.cssText = 'width:1px;background:var(--border-color);margin:0 2px;';
    this.actionBar.appendChild(sep);

    this.addActionBtn('Staff', 'staffList');
    this.addActionBtn('Company', 'company');
    this.addActionBtn('Log', 'log');
    this.addActionBtn('Awards', 'achievements');
    this.addActionBtn('Stats', 'statistics');
    this.addActionBtn('Save', 'save');
    this.addActionBtn('Load', 'load');

    // ─── Project Progress Bar ────────────────────────────────
    this.projectBar = document.createElement('div');
    this.projectBar.style.cssText = 'display:flex;gap:6px;padding:2px 12px;flex-wrap:wrap;pointer-events:auto;';

    this.releasesBar = document.createElement('div');
    this.releasesBar.style.cssText = 'display:flex;gap:4px;padding:2px 12px;flex-wrap:wrap;pointer-events:auto;overflow-x:auto;';
  }

  mount(parent: HTMLElement): void {
    // HUD container — sits at the top, pushes canvas down
    this.hudContainer = document.createElement('div');
    this.hudContainer.style.cssText = 'flex-shrink:0;pointer-events:auto;';
    this.hudContainer.appendChild(this.topBar);
    this.hudContainer.appendChild(this.actionBar);
    this.hudContainer.appendChild(this.projectBar);
    this.hudContainer.appendChild(this.releasesBar);
    parent.appendChild(this.hudContainer);

    // Measure HUD height and push the game canvas below it
    requestAnimationFrame(() => this.updateHudHeight());
  }

  /** Update the CSS variable that positions the canvas below the HUD. */
  private updateHudHeight(): void {
    const h = this.hudContainer.offsetHeight;
    document.documentElement.style.setProperty('--hud-height', `${h}px`);
  }

  update(state: SimulationState): void {
    this.fundsEl.textContent = formatCurrency(state.company.funds);
    this.fundsEl.className = state.company.funds < 0 ? 'hud__funds hud__funds--low' : 'hud__funds';
    this.dateEl.textContent = `Year ${state.company.currentYear} · Week ${state.company.currentWeek}`;
    this.staffEl.innerHTML = `<strong>${state.staff.length}</strong>/${state.office.maxStaff}`;
    this.levelEl.innerHTML = `Lv.<strong>${state.company.level}</strong>`;
    this.setActiveSpeed(state.speed);

    // Conditional buttons
    this.createTeamBtn.style.display = state.teams.length < state.office.maxTeams ? '' : 'none';
    const idleTeams = state.teams.filter((t) => t.memberIds.length > 0 && !t.assignedProjectId);
    const activeProjects = state.projects.filter((p) => p.phase !== 'complete');
    const canStartProject = idleTeams.length > 0 && activeProjects.length < state.office.maxConcurrentProjects;
    this.startProjectBtn.style.display = canStartProject ? '' : 'none';
    if (canStartProject) {
      this.startProjectBtn.textContent = idleTeams.length > 1 ? `+ Project (${idleTeams.length} idle)` : '+ Project';
    }
    this.hireBtn.style.display = state.staff.length < state.office.maxStaff ? '' : 'none';
    this.trainBtn.style.display = state.staff.length > 1 ? '' : 'none';

    // Contracts badge
    const contractCount = state.contractPool.length;
    this.contractsBtn.textContent = contractCount > 0 ? `Contracts (${contractCount})` : 'Contracts';
    this.upgradeBtn.style.display = state.office.upgradeCost > 0 && state.staff.length >= state.office.maxStaff - 1 ? '' : 'none';

    // Keep canvas positioned below HUD
    this.updateHudHeight();

    // Active projects + contracts + releases — only rebuild when data changes
    const projectFingerprint = [
      ...activeProjects.map((p) => `${p.id}:${p.phase}:${Math.round(p.phaseProgress)}:${Math.round(p.quality)}:${Math.round(p.bugs)}`),
      ...state.contracts.filter((c) => c.isActive).map((c) => `c${c.id}:${Math.round(c.progress)}:${c.assignedStaffIds.length}:${c.deadlineTicks}`),
      `r${state.products.length}`,
    ].join('|');
    if (projectFingerprint === this.lastProjectFingerprint) return;
    this.lastProjectFingerprint = projectFingerprint;
    this.projectBar.innerHTML = '';

    const statColors: Record<string, string> = {
      coding: '#5b9bd5', art: '#e88ab4', sound: '#f5d76e', design: '#4ecca3',
    };
    const statEmoji: Record<string, string> = {
      coding: '💻', art: '🎨', sound: '🎵', design: '📐',
    };

    for (const project of activeProjects) {
      const el = document.createElement('div');
      el.style.cssText = 'background:var(--color-bg-surface);border:1px solid var(--border-color);border-radius:3px;padding:3px 6px;font-size:10px;display:flex;flex-direction:column;gap:2px;min-width:140px;cursor:pointer;';
      el.addEventListener('click', () => this.onProjectClick?.(project.id));

      // Name + team + phase row
      const team = state.teams.find((t) => t.assignedProjectId === project.id);
      const teamLabel = team ? `<span style="color:var(--color-text-muted);font-size:8px;"> [${team.name}]</span>` : '';
      const nameRow = document.createElement('div');
      nameRow.style.cssText = 'display:flex;justify-content:space-between;gap:6px;';
      nameRow.innerHTML = `<strong style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${project.name}${teamLabel}</strong><span style="color:var(--color-text-muted);text-transform:capitalize;flex-shrink:0;font-size:9px;">${project.phase} ${Math.round(project.phaseProgress)}%</span>`;
      el.appendChild(nameRow);

      // Stat bars — the main visual feedback
      const maxStat = Math.max(...Object.values(project.progress), 1);
      const statsGrid = document.createElement('div');
      statsGrid.style.cssText = 'display:grid;grid-template-columns:14px 1fr 28px;gap:1px;align-items:center;';

      for (const [stat, value] of Object.entries(project.progress)) {
        const emoji = document.createElement('span');
        emoji.textContent = statEmoji[stat] ?? '📊';
        emoji.style.fontSize = '8px';

        const bar = document.createElement('div');
        bar.style.cssText = 'height:4px;background:var(--color-bg-primary);border-radius:1px;overflow:hidden;';
        const fill = document.createElement('div');
        const pct = maxStat > 0 ? (value / maxStat) * 100 : 0;
        fill.style.cssText = `height:100%;width:${Math.min(100, pct)}%;background:${statColors[stat] ?? '#888'};transition:width 200ms;`;
        bar.appendChild(fill);

        const val = document.createElement('span');
        val.textContent = Math.round(value).toString();
        val.style.cssText = `font-size:8px;text-align:right;font-family:var(--font-pixel);color:${statColors[stat] ?? '#888'};`;

        statsGrid.appendChild(emoji);
        statsGrid.appendChild(bar);
        statsGrid.appendChild(val);
      }
      el.appendChild(statsGrid);

      // Quality + bugs row
      const qualRow = document.createElement('div');
      qualRow.style.cssText = 'display:flex;justify-content:space-between;font-size:8px;color:var(--color-text-muted);';
      const qualColor = project.quality >= 70 ? '#4ecca3' : project.quality >= 40 ? '#f5a623' : '#e94560';
      qualRow.innerHTML = `<span>Quality: <strong style="color:${qualColor}">${Math.round(project.quality)}%</strong></span><span>Bugs: ${Math.round(project.bugs)}</span>`;
      el.appendChild(qualRow);

      // Ship button for ready projects
      if (project.phase === 'ready') {
        const shipBtn = document.createElement('button');
        shipBtn.className = 'btn btn--primary';
        shipBtn.textContent = 'Ship Game';
        shipBtn.style.cssText = 'font-size:9px;padding:2px 8px;margin-top:2px;';
        shipBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.onShipProject?.(project.id);
        });
        el.appendChild(shipBtn);
        el.style.borderColor = 'var(--color-success)';
      }

      this.projectBar.appendChild(el);
    }

    // Active contracts
    for (const contract of state.contracts) {
      if (!contract.isActive) continue;
      const isPaused = contract.assignedStaffIds.length === 0;
      const weeksLeft = Math.max(0, Math.ceil(contract.deadlineTicks / 70));
      const isUrgent = weeksLeft <= 2;
      const borderColor = isPaused ? '#6a6a2a' : isUrgent ? '#6a2a2a' : '#4a6a4a';

      const el = document.createElement('div');
      el.style.cssText = `background:var(--color-bg-surface);border:1px solid ${borderColor};border-radius:3px;padding:2px 6px;font-size:10px;display:flex;flex-direction:column;gap:1px;min-width:100px;cursor:pointer;`;
      el.addEventListener('click', () => this.onAction?.('contracts'));

      const nameRow = document.createElement('div');
      nameRow.style.cssText = 'display:flex;justify-content:space-between;gap:4px;';
      const icon = isPaused ? '⏸' : '📋';
      const statusColor = isPaused ? 'var(--color-warning)' : 'var(--color-success)';
      nameRow.innerHTML = `<span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:${statusColor};">${icon} ${contract.clientName}</span><span style="color:var(--color-text-muted);font-family:var(--font-pixel);">${Math.round(contract.progress)}%</span>`;
      el.appendChild(nameRow);

      const bar = document.createElement('div');
      bar.className = 'progress-bar';
      bar.style.height = '4px';
      bar.innerHTML = `<div class="progress-bar__fill" style="width:${Math.min(100, contract.progress)}%;background:${isPaused ? 'var(--color-warning)' : 'var(--color-success)'};"></div>`;
      el.appendChild(bar);

      // Deadline
      const deadlineRow = document.createElement('div');
      deadlineRow.style.cssText = `font-size:8px;color:${isUrgent ? 'var(--color-danger)' : 'var(--color-text-muted)'};`;
      deadlineRow.textContent = `⏱ ${weeksLeft}wk${isUrgent ? ' ⚠' : ''}`;
      el.appendChild(deadlineRow);

      this.projectBar.appendChild(el);
    }

    // Recent releases (last 3 player products + recent competitor releases)
    this.releasesBar.innerHTML = '';
    const recentProducts = state.products.slice(-3).reverse();
    for (const product of recentProducts) {
      const el = document.createElement('div');
      const reviewColor = product.averageReview >= 8 ? '#4ecca3' : product.averageReview >= 5 ? '#e0e0e0' : '#e94560';
      el.style.cssText = `background:var(--color-bg-surface);border:1px solid var(--border-color);border-radius:3px;padding:2px 6px;font-size:9px;min-width:120px;cursor:pointer;`;
      el.addEventListener('click', () => this.onAction?.('company'));

      el.innerHTML = `
        <div style="display:flex;justify-content:space-between;gap:4px;">
          <strong style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${product.name}${product.isHallOfFame ? ' ★' : ''}</strong>
          <span style="color:${reviewColor};font-family:var(--font-pixel);flex-shrink:0;">${product.averageReview.toFixed(1)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;color:var(--color-text-muted);">
          <span>${formatCurrency(product.totalRevenue)}</span>
          <span>${product.totalSales.toLocaleString()} sold</span>
        </div>
      `;
      this.releasesBar.appendChild(el);
    }

    // Competitor releases (show most recent)
    for (const comp of state.competitors) {
      const recentComp = comp.products.filter((p) => p.isActive).slice(-1);
      for (const cp of recentComp) {
        const el = document.createElement('div');
        el.style.cssText = 'background:var(--color-bg-primary);border:1px solid #3a3a5a;border-radius:3px;padding:2px 6px;font-size:9px;min-width:100px;opacity:0.7;';
        el.innerHTML = `
          <div style="color:var(--color-text-muted);"><em>${comp.name}</em></div>
          <div>"${cp.name}" · ${cp.category}</div>
        `;
        this.releasesBar.appendChild(el);
      }
    }
  }

  private setActiveSpeed(speed: GameSpeed): void {
    for (const btn of this.speedButtons) {
      btn.classList.toggle('btn--active', Number(btn.getAttribute('data-speed')) === speed);
    }
  }

  private addSpan(parent: HTMLElement, cls: string, text: string): HTMLSpanElement {
    const el = document.createElement('span');
    el.className = cls;
    el.textContent = text;
    parent.appendChild(el);
    return el;
  }

  private makeSpacer(): HTMLSpanElement {
    const el = document.createElement('span');
    el.className = 'hud__spacer';
    return el;
  }

  private addActionBtn(label: string, action: HUDAction, extraClass?: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = `btn${extraClass ? ` ${extraClass}` : ''}`;
    btn.textContent = label;
    btn.addEventListener('click', () => this.onAction?.(action));
    this.actionBar.appendChild(btn);
    return btn;
  }

  destroy(): void {
    this.topBar.remove();
    this.actionBar.remove();
    this.projectBar.remove();
    this.releasesBar.remove();
  }
}
